import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, inject, input} from '@angular/core';
import {MatTooltipModule} from '@angular/material/tooltip';
import {select, Store} from '@ngrx/store';
import {User} from 'src/app/core/model/user';
import {selectTeamsMap} from 'src/app/core/store/teams/teams.selector';
import {selectUsersMap} from 'src/app/core/store/users/users.selector';
import {Team} from 'src/app/teams/shared/model/team';
import {OphAvatarComponent} from '../oph-avatar/oph-avatar.component';

interface CombinedItem {
  type: 'user' | 'other';
  id: string;
}

@Component({
  selector: 'oph-avatar-multi',
  standalone: true,
  imports: [AsyncPipe, MatTooltipModule, OphAvatarComponent],
  templateUrl: './oph-avatar-multi.component.html',
  styleUrl: './oph-avatar-multi.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OphAvatarMultiComponent {
  userIds = input<string[]>([]);
  teamsIds = input<string[]>([]);
  tooltipPosition = input<'above' | 'below' | 'left' | 'right'>('above');
  toolTipDisabled = input<boolean>(false);
  avatarSize = input<number>(28);
  maxVisible = input<number | undefined>(undefined);
  tooltipSeparator = input<'line' | 'comma'>('line');

  store$ = inject(Store);

  usersMap$ = this.store$.pipe(select(selectUsersMap));
  teamsMap$ = this.store$.pipe(select(selectTeamsMap));

  // Combined array of users and teams for iteration
  combinedItems = computed((): CombinedItem[] => {
    const userIds = this.userIds();
    const teamIds = this.teamsIds();
    const items: CombinedItem[] = [];

    // Add users
    userIds?.forEach(userId => {
      items.push({
        type: 'user',
        id: userId,
      });
    });

    // Add teams
    teamIds?.forEach(teamId => {
      items.push({
        type: 'other',
        id: teamId,
      });
    });

    return items;
  });

  // Computed signal for display logic with max visible limit
  displayData = computed(() => {
    const allItems = this.combinedItems();
    const maxVisible = this.maxVisible();

    if (!maxVisible || allItems.length <= maxVisible) {
      return {
        visibleItems: allItems,
        remainingCount: 0,
        showRemaining: false,
        remainingItems: [],
      };
    }

    return {
      visibleItems: allItems.slice(0, maxVisible),
      remainingCount: allItems.length - maxVisible,
      showRemaining: true,
      remainingItems: allItems.slice(maxVisible),
    };
  });

  // Method to generate tooltip text for remaining items
  getRemainingItemsTooltip(usersMap: Record<string, User>, teamsMap: Record<string, Team>): string {
    const remainingItems = this.displayData().remainingItems;

    const tooltipItems = remainingItems.map(item => {
      if (item.type === 'user') {
        const user = usersMap[item.id];
        return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
      } else {
        const team = teamsMap?.[item.id];
        return team ? team.name : 'Unknown Team';
      }
    });

    // Use either newlines or commas based on tooltipSeparator input
    const separator = this.tooltipSeparator() === 'line' ? '\n' : ', ';
    return tooltipItems.join(separator);
  }
}
